import axios from 'axios';
import React, { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getRegions } from '../lib/apis';
import { useTranslation } from 'react-i18next';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children?: ReactNode;
    handleSearch:any;
    setValueToSearch: any;
    title?: string;
    valueToSearch?: any,
    closeOnOverlayClick?: boolean;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    showCloseButton?: boolean;
}

const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    handleSearch,
    title,
    closeOnOverlayClick = true,
    size = 'md',
    setValueToSearch,
    showCloseButton = true,
}) => {
    const [regions, setRegions] = useState([])
    const [departements, setDepartements] = useState([])
    const [loading, setLoading] = useState(false)
    const { t, i18n } = useTranslation()
    // Empêcher le défilement de la page
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    // Gérer la fermeture avec la touche Escape
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget && closeOnOverlayClick) {
            onClose();
        }
    };

    const sizeClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
    };

    const updatRegions = async () => {
        setLoading(true)
        try {
            const { data } = await axios.get(`${getRegions}`)
            // console.log(data)
            setRegions(data.regions)
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)

        }
    }
    useEffect(() => {
        updatRegions()
    }, [])
    const handleRegionChange = async (id: number) => {
        try {
            const { data } = await axios.get(`${getRegions}/${id}/departements`)
            setValueToSearch("region_id",id)
            setDepartements(data.departements)
        } catch (error) {
            console.log(error)
        }
    }

    const handleDepartmentChange = async (id: number) => {
        setValueToSearch("departement_id",id)
    }

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={handleOverlayClick}
        >
            {/* Overlay animé */}
            <div
                className={`fixed inset-0 bg-black/50 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'
                    }`}
            />

            {/* Contenu du modal animé */}
            <div
                className={`relative w-full rounded-lg bg-white shadow-xl transition-all duration-300 ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                    } ${sizeClasses[size]}`}
            >
                {/* En-tête */}
                {(title || showCloseButton) && (
                    <div className="flex items-center justify-between border-b p-4">
                        {title && <h3 className="text-lg font-semibold">{title}</h3>}
                        {showCloseButton && (
                            <button
                                onClick={onClose}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                                aria-label="Fermer"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-6 w-6"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        )}
                    </div>
                )}

                {/* Corps */}
                <div className='border-2 p-5 rounded-sm'>
                    {loading ? <div>{t("chargement")}</div> :
                        <div className='mt-4 flex flex-col gap-2'>
                            <select onChange={(e) => {
                                handleRegionChange(Number(e.target.value))
                            }} className={`border-2 p-2 rounded-sm`} name="region" id="region">
                                <option value="">{t("select_region")}</option>
                                {regions.map((region: any) => (
                                    <option key={region.id} value={region.region_id}>{i18n.language === "fr" ? region.region_name : region.region_name_en}</option>
                                ))}
                            </select>
                        </div>}
                    <div className='mt-4 flex flex-col gap-2'>
                        <select onChange={(e) => {
                            handleDepartmentChange(Number(e.target.value))
                        }} className={`  border-2 p-2 rounded-sm`} name="departement" id="departement">
                            <option value="">{t("select_departement")}</option>
                            {departements.map((departement: any) => (
                                <option key={departement.id} value={departement.departement_id}>{i18n.language === "fr" ? departement.departement_name : departement.departement_name_en}</option>
                            ))}
                        </select>
                    </div>
                   
                    <div className=' mt-4 w-[100%] flex items-center gap-2 justify-between  '>
                        <input onChange={(e) => setValueToSearch("prix_min",e.target.value)} placeholder={t("prix_min")} type="number" className='border-2 p-2 rounded-sm border-gray-200' />
                        <input onChange={(e) => setValueToSearch("prix_max",e.target.value)} placeholder={t("prix_max")} type="number" className='border-2 p-2 rounded-sm border-gray-200' />
                    </div>
                </div>

                {/* Pied de page optionnel */}
                <div className="border-t p-4 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
                        {t("annuler")}
                    </button>
                    <button onClick={()=>handleSearch()} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                        {t("recherche")}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default Modal;